import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { ContainerWithSideBar } from '../../common/ContainerWithSideBar/ContainerWithSideBar';
import { getUgoData, UGOContainerVM } from './UGOContainerVM';
import { Redirect, Route, Switch, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { UGODesign } from '../UGODesign/UGODesign';
import { UGOInputs } from '../UGOInputs/UGOInputs';
import { mdiPencilRulerOutline, mdiWaves } from '@mdi/js';
import stores from 'cd-eft/models/Stores';
import './UGOContainer.scss';
import { DesignContainerVM } from '../../DesignContainer/DesignContainerVM';
import { appConfig } from 'cd-eft/config/AppConfig';
import { UGOSummary } from '../UGOSummary/UGOSummary';
import { UGOSimulateRunoff } from '../UGOSimulateRunoff/UGOSimulateRunoff';

const { ugoDesignStore, designStore } = stores.AppStore;

interface IUgoProps {
  addUgoEdits: any;
}

const UGOContainerComponent = (props: IUgoProps) => {
  const { path } = useRouteMatch();
  const { projectId }: any = useParams();
  const { ugoId }: any = useParams();
  const location = useLocation();

  const vmRef = useRef<UGOContainerVM | null>(null);
  if (!vmRef.current) {
    vmRef.current = new UGOContainerVM();
  }
  const vm = vmRef.current;

  const ugoNavigation = [
    {
      name: 'input',
      title: 'UGO Inputs',
      icon: mdiWaves,
      link: `/design/${projectId}/ugo/${ugoId}/input`,
    },
    {
      name: 'design',
      title: 'UGO Design',
      icon: mdiPencilRulerOutline,
      link: `/design/${projectId}/ugo/${ugoId}/design`,
    },
  ];

  const mapView = DesignContainerVM.mapView;
  useEffect(() => {
    if (ugoId > 0 && mapView) {
      getUgoData(ugoId);
    }
  }, [ugoId, mapView]);

  useEffect(() => {
    const step = location && location.pathname ? location.pathname.split('/').reverse()[0] : '';
    if (['input', 'design'].includes(step)) {
      designStore.designElementTypeId = appConfig.hardCodes.designElementTypeUGO; //To enable the PEM Toolbar in the map view during edit UGO.
      vm.getErrors();
      vm.getPipelineMaterialTypes();
      vm.getFixtureTypes();
    }
  }, []);

  return (
    <WrappedUgoRoute ugoNavigation={ugoNavigation} location={location}>
      <Switch>
        <Route path={`${path}`} exact>
          <Redirect to={`/design/${projectId}/ugo/${ugoId}/input`} />
        </Route>
        <Route path={`${path}/input`}>
          <UGOInputs />
        </Route>
        <Route path={`${path}/design`}>
          <UGODesign addUgoEdits={props.addUgoEdits} mapView={mapView} />
        </Route>
      </Switch>
    </WrappedUgoRoute>
  );
};
export const UGOContainer = observer(UGOContainerComponent);

type ugoDesignStep = 'input' | 'design';
const ugoDesignStepConfig = {
  input: 'Underground Outlet (UGO) - Input',
  design: 'Underground Outlet (UGO) - Design',
};

const WrappedUgoRoute = observer(({ children, ugoNavigation, location }) => {
  const step: ugoDesignStep = location && location.pathname ? location.pathname.split('/').reverse()[0] : '';
  const ugoName = ugoDesignStore.ugoDesignObject.alignedFeatureName;
  let designStepTitle = ugoDesignStepConfig[step];
  if (ugoName) designStepTitle += ` - ${ugoName}`;

  return (
    <ContainerWithSideBar
      title={designStepTitle}
      navigation={ugoNavigation}
      tab={step}
      collapseMenuClass={'ugo-container'}
      isCollapsed={ugoDesignStore.ugoSidebarIsCollapsed}
      setCollapsed={ugoDesignStore.setUgoSideBarCollapsed}
    >
      <div className={`tab-pane-common tab-pane-common-${step} tab-pane h-100 active`}>{children}</div>
    </ContainerWithSideBar>
  );
});

export const UGOPages = observer(({ addUgoEdits }) => {
  const { path } = useRouteMatch();
  useEffect(() => {
    return () => ugoDesignStore.reset();
  }, []);

  return (
    <>
      <Switch>
        <Route path={`${path}/:ugoId`}>
          <UGOContainer addUgoEdits={addUgoEdits} />
        </Route>
        <Route exact path={`${path}`}>
          <UGOSummary />
        </Route>
      </Switch>

      {ugoDesignStore.simulateRunoffIsOpen && <UGOSimulateRunoff />}
    </>
  );
});
